import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, Check, Eye, EyeOff, Link2, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import { RichTextContent } from '../components/RichTextContent.js';
import { weeklyReportApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 対象週（YYYY-MM-DD）を「YYYY/MM/DD週」表記に整形する。
 * @graph-connects none
 */
function formatWeekOf(weekOf: string, t: TFunction): string {
  const [y, m, d] = weekOf.split('-');
  return y && m && d ? t('weeklyReport.weekOf', { date: `${y}/${m}/${d}` }) : weekOf;
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 週次レポート詳細ページ。サニタイズ済み本文 HTML を表示する。編集者には編集・公開/公開取消・削除の操作を表示する。
 * @graph-connects weeklyReportApi [calls] 週次レポート詳細・公開・削除 API
 * @graph-connects queryKeys [calls] react-query キーファクトリ
 */
export function WeeklyReportDetailPage() {
  const { t } = useTranslation();
  const params = useParams({ strict: false });
  const id = (params as { id?: string }).id ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [copied, setCopied] = useState(false);

  const copyShareUrl = () => {
    const url = window.location.href;
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.weeklyReports.detail(id),
    queryFn: () => weeklyReportApi.get(id),
    enabled: id !== '',
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.weeklyReports.all });
  };

  const publishMutation = useMutation({
    mutationFn: () => weeklyReportApi.publish(id),
    onSuccess: invalidate,
  });
  const unpublishMutation = useMutation({
    mutationFn: () => weeklyReportApi.unpublish(id),
    onSuccess: invalidate,
  });
  const deleteMutation = useMutation({
    mutationFn: () => weeklyReportApi.remove(id),
    onSuccess: () => {
      invalidate();
      void navigate({ to: '/weekly-report' });
    },
  });

  const canEdit = data?.canEdit ?? false;
  const report = data?.report;

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <Link
        to="/weekly-report"
        className="mb-6 inline-flex items-center gap-1 text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('common.backToList')}
      </Link>

      {isLoading ? (
        <p className="text-sm text-[var(--color-text-muted)]">{t('app.loading')}</p>
      ) : error || !report ? (
        <Card className="p-6 text-sm text-[var(--color-error)]">{t('weeklyReport.notFound')}</Card>
      ) : (
        <article>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-mono text-xs text-[var(--color-text-muted)]">
                {formatWeekOf(report.weekOf, t)}
                {canEdit && report.status === 'draft' ? t('weeklyReport.draftSuffix') : ''}
              </div>
              <h1 className="mt-1 text-2xl font-bold text-[var(--color-text-primary)]">
                {report.title}
              </h1>
            </div>
            <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                leftIcon={
                  copied ? (
                    <Check className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Link2 className="h-4 w-4" aria-hidden="true" />
                  )
                }
                onClick={copyShareUrl}
              >
                {copied ? t('common.copied') : t('weeklyReport.copyUrl')}
              </Button>
              {canEdit ? (
                <>
                  <Link to="/weekly-report/$id/edit" params={{ id: report.id }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={<Pencil className="h-4 w-4" aria-hidden="true" />}
                    >
                      {t('common.edit')}
                    </Button>
                  </Link>
                  {report.status === 'published' ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={unpublishMutation.isPending}
                      leftIcon={<EyeOff className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => {
                        unpublishMutation.mutate();
                      }}
                    >
                      {t('weeklyReport.unpublish')}
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      size="sm"
                      loading={publishMutation.isPending}
                      leftIcon={<Eye className="h-4 w-4" aria-hidden="true" />}
                      onClick={() => {
                        publishMutation.mutate();
                      }}
                    >
                      {t('weeklyReport.publish')}
                    </Button>
                  )}
                  <Button
                    variant="danger"
                    size="sm"
                    loading={deleteMutation.isPending}
                    leftIcon={<Trash2 className="h-4 w-4" aria-hidden="true" />}
                    onClick={() => {
                      if (window.confirm(t('weeklyReport.deleteConfirm'))) {
                        deleteMutation.mutate();
                      }
                    }}
                  >
                    {t('common.delete')}
                  </Button>
                </>
              ) : null}
            </div>
          </div>

          <hr className="my-6 border-[var(--color-border)]" />

          {/* 本文は tiptap JSON を読み取り専用エディタで描画（HTML 直挿入を避け XSS リスクなし）。bodyHtml は検索/通知用の派生として別途保存している。 */}
          <RichTextContent bodyJson={report.bodyJson} />
        </article>
      )}
    </div>
  );
}
